import { createContext, useContext, useState, useMemo, useEffect, useCallback } from "react";
import { useAuth } from "./AuthContext";
import EstadoConfirmModal from "../modules/pedidos/components/EstadoConfirmModal";

const CARRITO_KEY = "urualum_carrito";

function leerCarritoGuardado() {
  try {
    const guardado = localStorage.getItem(CARRITO_KEY);
    return guardado ? JSON.parse(guardado) : [];
  } catch {
    return [];
  }
}

// Si tenés un componente Toast por default, lo ignoramos aquí.
// Proveemos un wrapper simple compatible con toast.success/error/info.
const toast = {
  success: (m) => (window?.toast?.success ? window.toast.success(m) : console.log(m)),
  error:   (m) => (window?.toast?.error   ? window.toast.error(m)   : console.warn(`Error: ${m}`)),
  info:    (m) => (window?.toast?.info    ? window.toast.info(m)    : console.log(m)),
};

// Haptics opcional (si no existe, devolvemos stubs)
function useHapticsStub() {
  return {
    success: () => {},
    error: () => {},
    impact: () => {},
  };
}

const Ctx = createContext(null);

export function AppProvider({ children }) {
  const { profile } = useAuth();

  // ==========================================
  // ESTADOS APP INTERNA DE STOCK (NO TOCAR)
  // ==========================================
  const [depositoActual, setDepositoActual] = useState(null);
  const [metodoFiltro, setMetodoFiltro] = useState(null);
  const haptics = useHapticsStub();

  // Modal global de confirmación al avanzar de estado (suena + OK).
  // Lo dispara cualquier vista con confirmarAvance({ titulo, mensaje, onOk }).
  const [avanceModal, setAvanceModal] = useState(null);
  const confirmarAvance = useCallback((data) => setAvanceModal(data || null), []);
  const cerrarAvance = useCallback(() => {
    setAvanceModal((prev) => {
      // Ejecutar el callback (ej: volver a la lista) al cerrar
      if (prev?.onOk) { try { prev.onOk(); } catch (_) {} }
      return null;
    });
  }, []);

  // Sincronizar depositoActual con el depósito del usuario logueado.
  // Normalizamos a mayúsculas para que coincida con los valores en Firestore (ej: "ISABELA", "R8").
  useEffect(() => {
    const dep = profile?.deposito;
    if (dep) setDepositoActual(String(dep).trim().toUpperCase());
  }, [profile?.deposito]);

  // ==========================================
  // ESTADOS APP DE VENTAS / CLIENTES (NUEVO)
  // ==========================================
  const [usuarioApp, setUsuarioApp] = useState(null);
  const [reglasPrecios, setReglasPrecios] = useState(null);
  const [nombreLista, setNombreLista] = useState("");
  const [clienteActivo, setClienteActivo] = useState(null);

  // Lógica del Carrito
  const [carrito, setCarrito] = useState(leerCarritoGuardado);

  // Persistir en localStorage cada vez que el carrito cambia
  useEffect(() => {
    localStorage.setItem(CARRITO_KEY, JSON.stringify(carrito));
  }, [carrito]);

  const agregarAlCarrito = (item) => setCarrito((prev) => [...prev, item]);
  
  const eliminarDelCarrito = (index) => setCarrito((prev) => prev.filter((_, i) => i !== index));
  
  const limpiarCarrito = () => setCarrito([]);

  const actualizarCantidadItem = (index, nuevaCantidad) => {
    if (nuevaCantidad < 1) return;
    setCarrito((prev) => {
      const nuevoCarrito = [...prev];
      // Clonamos el item específico para que React detecte el cambio correctamente
      nuevoCarrito[index] = { ...nuevoCarrito[index] }; 
      nuevoCarrito[index].cantidad = nuevaCantidad;
      nuevoCarrito[index].precioTotal = nuevoCarrito[index].precioUnitario * nuevaCantidad;
      return nuevoCarrito;
    });
  };

  // Matemática del Carrito
  const subtotalCarrito = carrito.reduce((acc, item) => acc + item.precioTotal, 0);
  const ivaCarrito = subtotalCarrito * 0.22;
  const totalConIva = subtotalCarrito + ivaCarrito;

  // ==========================================
  // EXPORTACIÓN DE VARIABLES (CONSOLIDADO)
  // ==========================================
  const value = useMemo(() => ({
    // Variables de la App Interna
    depositoActual, setDepositoActual,
    metodoFiltro, setMetodoFiltro,
    haptics, toast,
    confirmarAvance,

    // Variables de la App Ventas
    usuarioApp, setUsuarioApp,
    reglasPrecios, setReglasPrecios,
    nombreLista, setNombreLista,
    clienteActivo, setClienteActivo,
    carrito, agregarAlCarrito, eliminarDelCarrito, limpiarCarrito,
    actualizarCantidadItem, // <-- Acá está la función exportada para que Carrito.js la pueda usar
    subtotalCarrito, ivaCarrito, totalConIva
  }), [
    // Array de dependencias del useMemo
    depositoActual, metodoFiltro,
    usuarioApp, reglasPrecios, nombreLista, carrito, 
    subtotalCarrito, ivaCarrito, totalConIva,
    confirmarAvance,
  ]);

  return (
    <Ctx.Provider value={value}>
      {children}
      {avanceModal && (
        <EstadoConfirmModal
          titulo={avanceModal.titulo}
          mensaje={avanceModal.mensaje}
          onOk={cerrarAvance}
        />
      )}
    </Ctx.Provider>
  );
}

// Hook recomendado
export const useApp = () => useContext(Ctx);

// ⚠️ Export named para compatibilidad con imports viejos
export const AppContext = Ctx;